import React from "react";
import CustomTooltip from "../../../../components/common/CustomTooltip";

interface WorkUnitResourceAvtarProps {
  name?: string | null;
  imageUrl?: string | null;
  className?: string;
  resourceId?: string; // Resource ID to show when name is not available
}

const getInitials = (fullName?: string) => {
  if (!fullName) return "??";

  const parts = fullName.trim().split(" ").filter(Boolean);

  if (parts.length > 1) {
    // Take first letter of first and last word
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // If only one word, take first 2 letters
  return parts[0].slice(0, 2).toUpperCase();
};

export const WorkUnitResourceAvtar = ({
  name,
  imageUrl,
  className = "",
  resourceId,
}: WorkUnitResourceAvtarProps) => {
  const [imageError, setImageError] = React.useState(false);

  // Use name if available, otherwise use resourceId
  const displayName = name || resourceId;
  const initials = getInitials(displayName);

  const avatarContent = (
    <div
      className={`relative w-[30px] h-[30px] rounded-full border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform duration-200 cursor-pointer ${className}`}
    >
      {imageUrl && !imageError ? (
        <img
          src={imageUrl}
          alt={displayName}
          className="w-full h-full rounded-full object-cover"
          onError={() => {
            // Fallback to initials if image fails to load
            setImageError(true);
          }}
        />
      ) : (
        <div className="w-full h-full rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-white text-xs font-medium">
          {initials}
        </div>
      )}
    </div>
  );

  // Show tooltip if we have either name or resourceId
  if (!displayName) return avatarContent;

  return (
    <CustomTooltip title={displayName} placement="top">
      {avatarContent}
    </CustomTooltip>
  );
};


export const ResourceAvatarCounter = ({
  count,
  remainingNames,
  className = "",
}: {
  count: number;
  remainingNames?: string[];
  className?: string;
}) => {
  const tooltipText =
    remainingNames && remainingNames.length > 0
      ? `Additional members: ${remainingNames.join(", ")}`
      : `${count} more member${count > 1 ? "s" : ""}`;

  const counterContent = (
    <div
      className={`relative w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-orange-500 dark:bg-orange-600 flex items-center justify-center text-white text-sm font-medium hover:scale-110 transition-transform duration-200 cursor-pointer ${className}`}
    >
      +{count}
    </div>
  );

  return (
    <CustomTooltip title={tooltipText} placement="top">
      {counterContent}
    </CustomTooltip>
  );
};
